import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Button, Card, StatusPill, Switch } from '@/components/ui';
import { cn, formatPrice } from '@/lib/utils';
import {
  useBulkCreationStore,
  type BulkReferenceProduct,
  type BulkReferenceProductColor,
} from '@/stores/bulkCreationStore';
import { ProductInfoDialog } from './ProductInfoDialog';

export interface ReferenceProductPanelProps {
  className?: string;
}

/** How many swatches a row shows before the rest collapses into "+N". */
export const MAX_VISIBLE_SWATCHES = 12;

/** Shown where the listing stores no price; needs no translation. */
const NO_PRICE = '—';

/** Fallback fill for a colour without a hex code. */
const UNKNOWN_COLOR = 'transparent';

/**
 * The colours of one product as small circles plus their number. `count` is
 * the authoritative total, which can exceed the swatches the API sends, so
 * the "+N" counts from it. Colours are catalogue data, so the accessible
 * name goes through i18next with the colour as a value ("Farbe Schwarz"),
 * never as bare text.
 */
function ColorSwatches({
  colors,
  count,
}: {
  colors: BulkReferenceProductColor[];
  count: number;
}) {
  const { t } = useTranslation();
  const visible = colors.slice(0, MAX_VISIBLE_SWATCHES);
  // Without swatches the count alone says everything; "+N" would repeat it.
  const overflow = visible.length > 0 ? Math.max(count - visible.length, 0) : 0;

  return (
    <>
      {visible.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {visible.map((color) => {
            const label = t('seller.bulk.reference.products.colorSwatch', {
              name: color.name,
            });
            return (
              <span
                key={color.productColorId}
                role="img"
                aria-label={label}
                title={label}
                style={{ backgroundColor: color.hexCode ?? UNKNOWN_COLOR }}
                className="h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-inset ring-line"
              />
            );
          })}
        </div>
      )}
      {overflow > 0 && (
        <span className="text-mute">
          {t('seller.bulk.reference.products.moreColors', {
            hidden: overflow,
          })}
        </span>
      )}
      <span className="text-mute">
        {t('seller.bulk.reference.products.colorCount', { count })}
      </span>
    </>
  );
}

/**
 * One money figure of a product row; a dash where the value is unknown
 * (no stored price, or a cost that is not maintained).
 */
function PriceFigure({
  labelKey,
  value: amount,
}: {
  labelKey: 'purchase' | 'selling' | 'margin';
  value: number | null | undefined;
}) {
  const { t } = useTranslation();
  const value = amount == null ? NO_PRICE : formatPrice(amount);

  return (
    <div className="flex items-baseline gap-1">
      <dt
        className="text-mute"
        title={t(`seller.bulk.reference.products.pricing.${labelKey}Hint`)}
      >
        {t(`seller.bulk.reference.products.pricing.${labelKey}`)}
      </dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

/**
 * Colours and EK/VK/Marge of one product on a single line below its name:
 * swatches, the colour count and the three figures share one wrapping row,
 * which keeps a row of the panel two lines high. At narrow widths the row
 * breaks between its parts instead of overflowing the column.
 */
function ProductFacts({ product }: { product: BulkReferenceProduct }) {
  const colors = product.colors ?? [];
  const colorCount = product.activeColorCount ?? colors.length;
  const pricing = product.pricing ?? null;

  return (
    <div
      data-testid={`bulk-product-facts-${product.productId}`}
      className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[12px]"
    >
      {colorCount > 0 && <ColorSwatches colors={colors} count={colorCount} />}
      <dl className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <PriceFigure labelKey="purchase" value={pricing?.purchase} />
        <PriceFigure labelKey="selling" value={pricing?.selling} />
        <PriceFigure labelKey="margin" value={pricing?.margin} />
      </dl>
    </div>
  );
}

/**
 * Product choice of the selected reference (R11, R13, R46): every product
 * with a switch, on by default, with its colours as swatches and the EK, VK
 * and Marge it inherits from the reference; products without a back design
 * are marked "nur Front", products without an active colour are disabled
 * with the reason. An "i" next to every name opens the product
 * information. Reads and writes the bulk store; the dialog fetches on its
 * own.
 */
export function ReferenceProductPanel({
  className,
}: ReferenceProductPanelProps) {
  const { t } = useTranslation();
  const reference = useBulkCreationStore((state) => state.reference);
  const activeProductIds = useBulkCreationStore(
    (state) => state.activeProductIds
  );
  const toggleProduct = useBulkCreationStore((state) => state.toggleProduct);
  /** Row whose information dialog is open; `null` keeps it closed. */
  const [infoProduct, setInfoProduct] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const selectable =
    reference?.products.filter((product) => product.disabledReason === null) ??
    [];
  const activeCount = selectable.filter((product) =>
    activeProductIds.includes(product.productId)
  ).length;

  return (
    <Card
      aria-labelledby="bulk-reference-products-title"
      role="region"
      className={cn('flex flex-col gap-4', className)}
    >
      <div className="flex flex-col gap-1.5">
        <h3
          id="bulk-reference-products-title"
          className="dc-h3 text-base text-ink"
        >
          {t('seller.bulk.reference.products.title')}
        </h3>
        <p className="text-sm text-ink-2">
          {reference
            ? t('seller.bulk.reference.products.intro')
            : t('seller.bulk.reference.products.placeholder')}
        </p>
      </div>

      {reference && (
        <>
          <p className="font-mono text-[12.5px] text-mute" aria-live="polite">
            {t('seller.bulk.reference.products.selectedCount', {
              count: activeCount,
              total: selectable.length,
            })}
          </p>

          <ul className="flex flex-col divide-y divide-line">
            {reference.products.map((product) => {
              const disabled = product.disabledReason !== null;
              const checked =
                !disabled && activeProductIds.includes(product.productId);
              return (
                <li
                  key={product.productId}
                  className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 py-2.5"
                >
                  <div
                    className={cn(
                      'flex min-w-0 flex-1 basis-[11rem] flex-col gap-1.5',
                      disabled && 'opacity-60'
                    )}
                  >
                    <div className="flex min-w-0 items-center gap-1">
                      <span className="truncate text-sm font-medium text-ink">
                        {product.name}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="h-6 w-6 shrink-0 text-mute"
                        onClick={() =>
                          setInfoProduct({
                            id: product.productId,
                            name: product.name,
                          })
                        }
                        aria-label={t(
                          'seller.bulk.reference.products.info.open',
                          { name: product.name }
                        )}
                      >
                        <Info className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </div>
                    {(!product.hasBack || disabled) && (
                      <div className="flex flex-wrap items-center gap-2">
                        {!product.hasBack && (
                          <StatusPill
                            variant="neutral"
                            title={t(
                              'seller.bulk.reference.products.onlyFrontHint'
                            )}
                          >
                            {t('seller.bulk.reference.products.onlyFront')}
                          </StatusPill>
                        )}
                        {product.disabledReason !== null && (
                          <span className="text-xs text-mute">
                            {t(
                              `seller.bulk.reference.products.disabledReasons.${product.disabledReason}`
                            )}
                          </span>
                        )}
                      </div>
                    )}
                    <ProductFacts product={product} />
                  </div>
                  <Switch
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={() => toggleProduct(product.productId)}
                    aria-label={t('seller.bulk.reference.products.toggle', {
                      name: product.name,
                    })}
                    className="mt-0.5 shrink-0"
                  />
                </li>
              );
            })}
          </ul>

          {activeCount === 0 && (
            <p role="alert" className="text-sm text-danger">
              {t('seller.bulk.reference.products.noneSelected')}
            </p>
          )}

          <ProductInfoDialog
            productId={infoProduct?.id ?? null}
            productName={infoProduct?.name ?? ''}
            onOpenChange={(open) => {
              if (!open) setInfoProduct(null);
            }}
          />
        </>
      )}
    </Card>
  );
}
